// ---------------------------------------------------------------------------
// Organizer reads and writes: turf creation, population, and assignment.
//
// Every mutation here is gated twice -- by is_organizer() inside the RLS
// policy or the RPC, and by the UI only offering these screens to organizers.
// The first is the security boundary; the second is courtesy (PLAN 4.6).
// ---------------------------------------------------------------------------

import type { Profile, Role, Turf } from '@yucanvas/shared';
import { fetchAllPages } from '../lib/paginate';
import { supabase } from '../lib/supabase';

/** Just enough of a household to draw the town and see what is claimed. */
export interface HouseholdPoint {
  id: string;
  lat: number;
  lon: number;
  turf_id: string | null;
}

export interface TurfDetail extends Turf {
  household_count: number;
  /** Households with at least one visit, of any outcome. */
  knocked_count: number;
  canvassers: Pick<Profile, 'id' | 'name'>[];
}

/**
 * PostGIS geometry accepts EWKT through PostgREST. The ring must be closed --
 * Postgres rejects an open polygon, and the draw tool tracks vertices, not a
 * closed ring.
 */
export function ringToEwkt(ring: readonly [number, number][]): string {
  if (ring.length < 3) throw new Error('A turf needs at least three corners.');

  const closed = [...ring];
  const first = closed[0]!;
  const last = closed[closed.length - 1]!;
  if (first[0] !== last[0] || first[1] !== last[1]) closed.push(first);

  const points = closed.map(([lon, lat]) => `${lon} ${lat}`).join(',');
  return `SRID=4326;POLYGON((${points}))`;
}

export async function fetchHouseholdPoints(): Promise<HouseholdPoint[]> {
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('households')
      .select('id,lat,lon,turf_id')
      .eq('active', true)
      .order('id')
      .range(from, to),
  );
  return rows as unknown as HouseholdPoint[];
}

export async function fetchTurfDetails(): Promise<TurfDetail[]> {
  const { data: turfs, error } = await supabase.from('turfs').select('*').order('name');
  if (error) throw error;

  const { data: assignments, error: assignError } = await supabase
    .from('turf_assignments')
    // turf_assignments points at profiles twice, through canvasser_id and
    // assigned_by, so the embed has to name which one it means.
    .select('turf_id, profiles!canvasser_id(id, name)');
  if (assignError) throw assignError;

  // One grouped query, not one count per turf. This was thirty concurrent
  // count(*) round trips at roughly half a second each, re-run on every reload
  // of a screen that reloads after every turf mutation (20260912000100). Both
  // numbers come back together so the map's shading and the sidebar's counts
  // can never be a render apart (20260915000200).
  const { data: countRows, error: countError } = await supabase.rpc(
    'turf_coverage_counts',
  );
  if (countError) throw countError;

  // A turf with no households is absent from a GROUP BY rather than zero.
  const counts = new Map(
    (countRows ?? []).map((r) => [
      r.out_turf_id,
      {
        households: Number(r.out_household_count),
        knocked: Number(r.out_knocked_count),
      },
    ]),
  );

  return (turfs ?? []).map((t) => ({
    ...t,
    household_count: counts.get(t.id)?.households ?? 0,
    knocked_count: counts.get(t.id)?.knocked ?? 0,
    canvassers: (assignments ?? [])
      .filter((a) => a.turf_id === t.id)
      .flatMap((a) => (a.profiles ? [a.profiles as Pick<Profile, 'id' | 'name'>] : [])),
  }));
}

/** Active canvassers, for the assignment picker. */
export async function fetchRoster(): Promise<Profile[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('active', true)
    .order('name');
  if (error) throw error;
  return (data ?? []) as Profile[];
}

/**
 * The roles the dashboard may hand out. Not `admin`: it is issued by
 * scripts/seed-admin.ts with the service_role key, and profiles_manage
 * (20261028000100) refuses an organizer who tries to grant it or to change an
 * account that holds it.
 */
export type AssignableRole = Extract<Role, 'canvasser' | 'organizer'>;

/**
 * Promotes a canvasser to organizer, or puts an organizer back.
 *
 * An ordinary table write under the profiles_manage policy
 * (20260901000300_rls.sql) -- roles were always meant to be managed from the
 * dashboard (docs/deviations-from-plan.md), there was simply no control for it
 * until now. Turf assignments are left alone in both directions: a demoted
 * organizer keeps whatever streets they had, which is what makes this
 * reversible after a misclick.
 */
export async function setRole(profileId: string, role: AssignableRole): Promise<void> {
  const { error } = await supabase.from('profiles').update({ role }).eq('id', profileId);
  if (error) throw error;
}

export async function createTurf(
  name: string,
  ring: readonly [number, number][],
): Promise<string> {
  const { data, error } = await supabase
    .from('turfs')
    .insert({ name, boundary: ringToEwkt(ring) })
    .select('id')
    .single();
  if (error) throw error;
  return data.id;
}

export async function updateTurfBoundary(
  turfId: string,
  ring: readonly [number, number][],
): Promise<void> {
  const { error } = await supabase
    .from('turfs')
    .update({ boundary: ringToEwkt(ring) })
    .eq('id', turfId);
  if (error) throw error;
}

export async function renameTurf(turfId: string, name: string): Promise<void> {
  const { error } = await supabase.from('turfs').update({ name }).eq('id', turfId);
  if (error) throw error;
}

/**
 * Marks a turf finished, or reopens it. Completed turf drops off every
 * canvasser's list at their next sync (see myTurfIds() in offline/sync.ts);
 * its visits and households are untouched, so reopening loses nothing.
 */
export async function setTurfCompleted(turfId: string, completed: boolean): Promise<void> {
  const { error } = await supabase
    .from('turfs')
    .update({ completed_at: completed ? new Date().toISOString() : null })
    .eq('id', turfId);
  if (error) throw error;
}

/**
 * Claims every household inside the boundary and releases anything that no
 * longer falls in it. Returns the resulting size of the turf.
 */
export async function populateTurf(turfId: string): Promise<number> {
  const { data, error } = await supabase.rpc('assign_households_to_turf', {
    p_turf_id: turfId,
  });
  if (error) throw error;
  return data ?? 0;
}

/** Households return to the unassigned pool; visits are history and stay. */
export async function deleteTurf(turfId: string): Promise<void> {
  const { error } = await supabase.from('turfs').delete().eq('id', turfId);
  if (error) throw error;
}

export async function setAssignment(
  turfId: string,
  canvasserId: string,
  assigned: boolean,
): Promise<void> {
  if (assigned) {
    const { error } = await supabase
      .from('turf_assignments')
      .insert({ turf_id: turfId, canvasser_id: canvasserId });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from('turf_assignments')
      .delete()
      .eq('turf_id', turfId)
      .eq('canvasser_id', canvasserId);
    if (error) throw error;
  }
}

// ---------------------------------------------------------------------------
// Phase 6: coverage, activity, export, do-not-contact, manual units.
//
// The aggregates are computed on the client from full table reads, matching
// what fetchHouseholdPoints already does for the map. PostgREST cannot group,
// and the alternative is a reporting view per statistic; on a town-sized
// dataset, over a desktop connection, this stays the cheaper trade.
// ---------------------------------------------------------------------------

/** A visit reduced to what the dashboard aggregates over. */
interface VisitFact {
  household_id: string;
  canvasser_id: string;
  outcome: string;
  visited_at: string;
}

/**
 * A person contact reduced the same way. Support lives here rather than on the
 * visit since the campaign moved to person-level data
 * (docs/campaign-requirements.md § 3.1).
 */
interface ContactFact {
  person_id: string;
  support: string | null;
  spoke: boolean;
  sign_requested: boolean;
  contacted_at: string;
  people: { household_id: string } | null;
}

export interface TurfCoverage {
  turf_id: string;
  name: string;
  households: number;
  /** Distinct households with at least one visit of any outcome. */
  knocked: number;
  /** Distinct households where somebody actually answered the door. */
  spoke: number;
  /** Latest stated support per person, counted. Individuals, not households. */
  support: Record<string, number>;
}

export interface CanvasserActivity {
  id: string;
  name: string;
  role: string;
  active: boolean;
  last_sync_at: string | null;
  visits: number;
  /** Most recent visit this canvasser recorded, by their own clock. */
  last_visit_at: string | null;
}

async function fetchVisitFacts(): Promise<VisitFact[]> {
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('visits')
      .select('household_id,canvasser_id,outcome,visited_at')
      .order('visited_at')
      .range(from, to),
  );
  return rows as unknown as VisitFact[];
}

/**
 * Every contact, with the household reachable through the person, so support
 * can be attributed to a turf.
 */
async function fetchContactFacts(): Promise<ContactFact[]> {
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('person_contacts')
      .select('person_id,support,spoke,sign_requested,contacted_at,people(household_id)')
      .order('contacted_at')
      .range(from, to),
  );
  return rows as unknown as ContactFact[];
}

/** Newest contact per person wins: a later conversation supersedes an earlier. */
function latestByPerson(facts: readonly ContactFact[]): Map<string, ContactFact> {
  const latest = new Map<string, ContactFact>();
  for (const f of facts) {
    const seen = latest.get(f.person_id);
    if (!seen || f.contacted_at > seen.contacted_at) latest.set(f.person_id, f);
  }
  return latest;
}

export async function fetchCoverage(): Promise<TurfCoverage[]> {
  const [turfs, points, facts, contacts] = await Promise.all([
    supabase.from('turfs').select('id,name').order('name'),
    fetchHouseholdPoints(),
    fetchVisitFacts(),
    fetchContactFacts(),
  ]);
  if (turfs.error) throw turfs.error;

  const turfOf = new Map(points.map((p) => [p.id, p.turf_id]));
  const latest = latestByPerson(contacts);

  const knocked = new Map<string, Set<string>>();
  const spoke = new Map<string, Set<string>>();
  const support = new Map<string, Record<string, number>>();

  const bucket = (m: Map<string, Set<string>>, turfId: string, houseId: string) => {
    const set = m.get(turfId) ?? new Set<string>();
    set.add(houseId);
    m.set(turfId, set);
  };

  for (const f of facts) {
    const turfId = turfOf.get(f.household_id);
    // A visit whose household has since been released from every turf still
    // happened; it just has nowhere to be counted.
    if (!turfId) continue;
    bucket(knocked, turfId, f.household_id);
    if (f.outcome === 'answered') bucket(spoke, turfId, f.household_id);
  }

  for (const f of latest.values()) {
    const householdId = f.people?.household_id;
    if (!householdId || !f.support) continue;
    const turfId = turfOf.get(householdId);
    if (!turfId) continue;
    const tally = support.get(turfId) ?? {};
    tally[f.support] = (tally[f.support] ?? 0) + 1;
    support.set(turfId, tally);
  }

  const households = new Map<string, number>();
  for (const p of points) {
    if (p.turf_id) households.set(p.turf_id, (households.get(p.turf_id) ?? 0) + 1);
  }

  return (turfs.data ?? []).map((t) => ({
    turf_id: t.id,
    name: t.name,
    households: households.get(t.id) ?? 0,
    knocked: knocked.get(t.id)?.size ?? 0,
    spoke: spoke.get(t.id)?.size ?? 0,
    support: support.get(t.id) ?? {},
  }));
}

/**
 * Who has synced and who has not. The point of the table is PLAN 5.7: records
 * only move while the app is open, so a volunteer whose last sync was Saturday
 * is carrying a phone full of unsent knocks and needs a phone call.
 */
export async function fetchCanvasserActivity(): Promise<CanvasserActivity[]> {
  const [profiles, facts] = await Promise.all([
    supabase.from('profiles').select('*').order('name'),
    fetchVisitFacts(),
  ]);
  if (profiles.error) throw profiles.error;

  const counts = new Map<string, { n: number; last: string | null }>();
  for (const f of facts) {
    const seen = counts.get(f.canvasser_id) ?? { n: 0, last: null };
    seen.n += 1;
    if (!seen.last || f.visited_at > seen.last) seen.last = f.visited_at;
    counts.set(f.canvasser_id, seen);
  }

  return (profiles.data ?? []).map((p) => ({
    id: p.id,
    name: p.name,
    role: p.role,
    active: p.active,
    last_sync_at: p.last_sync_at,
    visits: counts.get(p.id)?.n ?? 0,
    last_visit_at: counts.get(p.id)?.last ?? null,
  }));
}

// --- CSV export ------------------------------------------------------------

/** Column order of the export. Stated once so the header cannot drift. */
const EXPORT_COLUMNS = [
  'visited_at',
  'received_at',
  'canvasser',
  'street_address',
  'unit',
  'city',
  'state',
  'postal_code',
  'lat',
  'lon',
  'address_source',
  'outcome',
  'inaccessible_reason',
  'literature',
  'best_return_time',
  'sign_requested',
  'volunteer_interest',
  'notes',
] as const;

interface ExportRow {
  visited_at: string;
  received_at: string;
  outcome: string;
  inaccessible_reason: string | null;
  literature: string;
  best_return_time: string | null;
  sign_requested: boolean;
  volunteer_interest: boolean;
  notes: string | null;
  households: {
    street_address: string;
    unit: string | null;
    city: string;
    state: string;
    postal_code: string | null;
    lat: number;
    lon: number;
    source: string;
  } | null;
  profiles: { name: string } | null;
}

/**
 * Every visit, flattened for a spreadsheet. This is the DOOR-level export:
 * one row per knock, carrying the outcome.
 *
 * Support is no longer here. It belongs to a person now
 * (docs/campaign-requirements.md § 3.1), so "who said they support us" needs a
 * person-level export over person_contacts -- see § 3.8, which the admin query
 * surface will cover.
 */
export async function fetchExportRows(): Promise<unknown[][]> {
  const rows = (await fetchAllPages((from, to) =>
    supabase
      .from('visits')
      .select(
        'visited_at,received_at,outcome,inaccessible_reason,literature,best_return_time,' +
          'sign_requested,volunteer_interest,notes,' +
          'households(street_address,unit,city,state,postal_code,lat,lon,source),' +
          'profiles(name)',
      )
      .order('visited_at')
      .range(from, to),
  )) as unknown as ExportRow[];

  return [
    [...EXPORT_COLUMNS],
    ...rows.map((r) => [
      r.visited_at,
      r.received_at,
      r.profiles?.name ?? '',
      r.households?.street_address ?? '',
      r.households?.unit ?? '',
      r.households?.city ?? '',
      r.households?.state ?? '',
      r.households?.postal_code ?? '',
      r.households?.lat ?? '',
      r.households?.lon ?? '',
      r.households?.source ?? '',
      r.outcome,
      r.inaccessible_reason ?? '',
      r.literature,
      r.best_return_time ?? '',
      r.sign_requested,
      r.volunteer_interest,
      r.notes ?? '',
    ]),
  ];
}

// --- do-not-contact and manual units ---------------------------------------


export interface FlaggedHousehold {
  id: string;
  street_address: string;
  unit: string | null;
  postal_code: string | null;
  turf_id: string | null;
  do_not_contact_reason: string | null;
}

export async function fetchFlagged(): Promise<FlaggedHousehold[]> {
  const rows = await fetchAllPages((from, to) =>
    supabase
      .from('households')
      .select('id,street_address,unit,postal_code,turf_id,do_not_contact_reason')
      .eq('do_not_contact', true)
      .eq('active', true)
      .order('street_address')
      .range(from, to),
  );
  return rows as unknown as FlaggedHousehold[];
}

/** Organizers only, enforced in the RPC: clearing re-exposes the address. */
export async function clearDoNotContact(householdId: string): Promise<void> {
  const { error } = await supabase.rpc('clear_do_not_contact', {
    p_household_id: householdId,
  });
  if (error) throw new Error(error.message);
}

export interface AddressMatch {
  id: string;
  street_address: string;
  unit: string | null;
  postal_code: string | null;
}

/** Address lookup for the unit picker. Escapes the wildcards PostgREST reads. */
export async function searchAddresses(query: string): Promise<AddressMatch[]> {
  const term = query.trim();
  if (term.length < 3) return [];

  // % and _ are wildcards in ilike, and a comma would end the filter value.
  const safe = term.replaceAll(/[%_,]/g, ' ');

  const { data, error } = await supabase
    .from('households')
    .select('id,street_address,unit,postal_code')
    .eq('active', true)
    .ilike('street_address', `%${safe}%`)
    .order('street_address')
    .limit(20);
  if (error) throw error;
  return (data ?? []) as AddressMatch[];
}

/**
 * Adds one unit at an existing address. The RPC copies the address and
 * position from the parent, so nothing here can move a household or invent an
 * address that no public dataset ever contained (PLAN 2.2).
 */
export async function addHouseholdUnit(
  parentId: string,
  unit: string,
): Promise<string> {
  const { data, error } = await supabase.rpc('add_household_unit', {
    p_parent_id: parentId,
    p_unit: unit,
  });
  if (error) throw new Error(error.message);
  return data as string;
}

// --- person query (docs/campaign-requirements.md § 3.2) ---------------------

import type { PersonQueryRow } from './person-query';

export {
  EMPTY_FILTERS,
  applyPersonFilters,
  personExportRows,
  type PersonFilters,
  type PersonQueryRow,
} from './person-query';

const PERSON_QUERY_COLUMNS =
  'id,first_name,last_name,suffix,party,gender,age,tier,registration_status,' +
  'vbm_status,precinct,do_not_call,deceased,moved,sign_delivered_at,household_id,' +
  'households!inner(street_address,unit,turf_id)';

interface RawPersonRow {
  id: string;
  first_name: string;
  last_name: string;
  suffix: string | null;
  party: string | null;
  gender: string | null;
  age: number | null;
  tier: string | null;
  registration_status: string | null;
  vbm_status: string | null;
  precinct: string | null;
  do_not_call: boolean;
  deceased: boolean;
  moved: boolean;
  sign_delivered_at: string | null;
  household_id: string;
  households: { street_address: string; unit: string | null; turf_id: string | null } | null;
}

/**
 * Every person the organizer can see, with their latest contact folded in.
 *
 * The whole District 1 universe is 4,858 rows, so this reads the lot and
 * filters in memory rather than pushing predicates into PostgREST. That keeps
 * "supporters who have not been contacted" -- a filter spanning two tables and
 * a fold -- expressible without a reporting view per question
 * (docs/campaign-requirements.md § 3.8). Revisit if the universe grows by an
 * order of magnitude.
 */
export async function fetchPeopleForQuery(): Promise<PersonQueryRow[]> {
  const [rows, contacts] = await Promise.all([
    // Inactive people are voters taken out of the walk universe on purpose
    // (see scripts/scope-to-district.ts), not merely unknocked ones. The id
    // tie-break keeps pages stable: thousands of rows share a last name
    // boundary, and ordering on it alone lets a page skip or repeat people.
    fetchAllPages((from, to) =>
      supabase
        .from('people')
        .select(PERSON_QUERY_COLUMNS)
        .eq('active', true)
        .order('last_name')
        .order('id')
        .range(from, to),
    ) as unknown as Promise<RawPersonRow[]>,
    fetchContactFacts(),
  ]);

  // Newest contact per person, plus whether anyone ever asked for a sign.
  const latest = latestByPerson(contacts);
  const counts = new Map<string, number>();
  // A yard sign asked for in March is still wanted in October, even if a later
  // conversation was about something else -- so this is "ever", not "latest".
  const everAskedForSign = new Set<string>();
  for (const c of contacts) {
    counts.set(c.person_id, (counts.get(c.person_id) ?? 0) + 1);
    if (c.sign_requested) everAskedForSign.add(c.person_id);
  }

  return rows.map((r) => {
    const last = latest.get(r.id);
    return {
      id: r.id,
      first_name: r.first_name,
      last_name: r.last_name,
      suffix: r.suffix,
      party: r.party,
      gender: r.gender,
      age: r.age,
      tier: r.tier,
      registration_status: r.registration_status,
      vbm_status: r.vbm_status,
      precinct: r.precinct,
      do_not_call: r.do_not_call,
      deceased: r.deceased,
      moved: r.moved,
      household_id: r.household_id,
      street_address: r.households?.street_address ?? '',
      unit: r.households?.unit ?? null,
      turf_id: r.households?.turf_id ?? null,
      support: last?.support ?? null,
      spoke: last?.spoke ?? false,
      sign_requested: everAskedForSign.has(r.id),
      sign_delivered: r.sign_delivered_at !== null,
      contact_count: counts.get(r.id) ?? 0,
    };
  });
}

/** Records a yard sign as dropped off, or takes that back. */
export async function setSignDelivered(
  personId: string,
  delivered: boolean,
): Promise<void> {
  const { error } = await supabase.rpc('set_sign_delivered', {
    p_person_id: personId,
    p_delivered: delivered,
  });
  if (error) throw new Error(error.message);
}
